import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { IconProps } from '@/features/global/components/icon/Icon';
import { useContext } from '@/hooks/useContext';
import { persistentStorage } from '@/lib/storage';
import {
  SharedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { rightSidebarConfig } from '~/layout/defs/rightSidebarConfig';

export interface RightSidebarTab {
  /** Stable id; persisted as the active tool and used for the active state. */
  key: string;
  /** lucide icon name (PascalCase). */
  icon: IconProps['name'];
  /** Tooltip + accessibility label. */
  label: string;
  /** Expanded panel content shown when this tab is active. */
  panel: ReactNode;
}

export interface RegisterRailOptions {
  /** Panel width when expanded; defaults to `rightSidebarConfig.dimensions.open`. */
  expandedWidth?: number;
}

export interface RightSidebarLayoutContext {
  /** Tabs registered by the active route, if any. */
  tabs: RightSidebarTab[] | null;
  /** Key of the open tab, or `null` when collapsed to the rail. */
  activeKey: string | null;
  /** Whether the active route registered a (non-empty) rail. */
  hasRail: boolean;
  /** The panel node of the active tab, or `null` when collapsed. */
  activePanel: ReactNode | null;
  /** Toggle a tab: opens it, or collapses if it is already the active one. */
  setActiveTab: (key: string) => void;
  /** Register this route's rail. `id` is the owner token (anti-flash). */
  registerRail: (
    id: string,
    tabs: RightSidebarTab[],
    options?: RegisterRailOptions,
  ) => void;
  /** Clear the rail, but only if `id` is still the current owner. */
  unregisterRail: (id: string) => void;
  /** 1 = rail docked, 0 = none (animated). */
  railVisibleState: SharedValue<number>;
  /** 1 = panel open, 0 = collapsed (animated). */
  panelExpandedState: SharedValue<number>;
  /** Current panel width (e.g. 320 / 340), driven on the UI thread. */
  panelWidthValue: SharedValue<number>;
}

const ctx = createContext<RightSidebarLayoutContext | undefined>(undefined);
ctx.displayName = 'RightSidebarLayout';

export interface RightSidebarLayoutProviderProps {
  children?: ReactNode;
}

export const RightSidebarLayoutProvider = ({
  children,
}: RightSidebarLayoutProviderProps) => {
  const [tabs, setTabs] = useState<RightSidebarTab[] | null>(null);
  // Default collapsed (no active tab); the persisted tool is loaded after mount.
  const [activeKey, setActiveKey] = useState<string | null>(null);
  // Owner token of whoever last registered a rail. Guards cleanup so navigating
  // between two routes that both declare a rail does not flash blank (the
  // outgoing route's cleanup no-ops once the incoming route has taken over).
  const ownerRef = useRef<string | null>(null);

  const railVisibleState = useSharedValue(0);
  const panelExpandedState = useSharedValue(0);
  const panelWidthValue = useSharedValue(rightSidebarConfig.dimensions.open);

  const registerRail = useCallback(
    (
      id: string,
      nextTabs: RightSidebarTab[],
      options?: RegisterRailOptions,
    ) => {
      ownerRef.current = id;
      panelWidthValue.value =
        options?.expandedWidth ?? rightSidebarConfig.dimensions.open;
      setTabs(nextTabs);
    },
    [panelWidthValue],
  );

  const unregisterRail = useCallback((id: string) => {
    if (ownerRef.current === id) {
      ownerRef.current = null;
      setTabs(null);
    }
  }, []);

  const setActiveTab = useCallback((key: string) => {
    setActiveKey((current) => {
      const next = current === key ? null : key;
      void persistentStorage.set('rightSidebarActiveTool', next);
      return next;
    });
  }, []);

  const hasRail = tabs != null && tabs.length > 0;

  // Animate the rail in/out when a route registers / unregisters it.
  useEffect(() => {
    railVisibleState.value = withTiming(hasRail ? 1 : 0, {
      duration: rightSidebarConfig.durations.expand,
    });
  }, [hasRail, railVisibleState]);

  // Drop the open tab when it is not part of the current route's rail (after
  // navigation, or a persisted tool this screen doesn't have). Done in-memory
  // only — the stored preference is left intact so the originating screen can
  // still restore it on reload. Guarded on `tabs != null` so the tab isn't
  // cleared before the route has registered its rail.
  useEffect(() => {
    if (
      activeKey != null &&
      tabs != null &&
      !tabs.some((tab) => tab.key === activeKey)
    ) {
      setActiveKey(null);
    }
  }, [tabs, activeKey]);

  // Animate the panel open/closed on the UI thread. Gated on `hasRail` so a
  // screen that registers no rail (e.g. the overview) never reserves the panel
  // width, even when a tool is still the persisted/active one — the preference
  // is kept in storage and restored when returning to a screen that has a rail.
  useEffect(() => {
    panelExpandedState.value = withTiming(
      hasRail && activeKey != null ? 1 : 0,
      {
        duration: rightSidebarConfig.durations.expand,
      },
    );
  }, [hasRail, activeKey, panelExpandedState]);

  const activePanel = useMemo(
    () => tabs?.find((tab) => tab.key === activeKey)?.panel ?? null,
    [tabs, activeKey],
  );

  const value = useMemo<RightSidebarLayoutContext>(
    () => ({
      tabs,
      activeKey,
      hasRail,
      activePanel,
      setActiveTab,
      registerRail,
      unregisterRail,
      railVisibleState,
      panelExpandedState,
      panelWidthValue,
    }),
    [
      tabs,
      activeKey,
      hasRail,
      activePanel,
      setActiveTab,
      registerRail,
      unregisterRail,
      railVisibleState,
      panelExpandedState,
      panelWidthValue,
    ],
  );

  // Restore the last-used tool. The effect above validates it against the
  // active route's rail (an unrelated stored key collapses to the rail).
  useEffect(() => {
    persistentStorage.get('rightSidebarActiveTool').then((stored) => {
      if (stored) {
        setActiveKey(stored);
      }
    });
  }, []);

  return <ctx.Provider value={value}>{children}</ctx.Provider>;
};

export const useRightSidebar = () => useContext(ctx);
